import { creatorNames } from "./credits";
import { formatTime, localDay } from "./dates";
import { segmentRange } from "./episodes";
import { pastVerb, progressVerb } from "./media";
import Log from "../types/Log";
import Tracking, { Status } from "../types/Tracking";
import Work, { WorkType } from "../types/Work";
import { groupingOf } from "./groupings";
import { byPriority } from "./wants";

// Whether a log was finished: its end date isn't a drop.
export const isFinish = (log: Log): boolean => !!log.finishedAt && !log.dropped;

// The most recent finish date across an entry's logs ("" → never finished).
const latestFinished = (entry: Tracking): string =>
  entry.logs.reduce(
    (latest, log) =>
      isFinish(log) && log.finishedAt! > latest ? log.finishedAt! : latest,
    "",
  );

// Completed shelf order: most recently finished first, ties broken by recency.
export const byCompletion = (a: Tracking, b: Tracking): number => {
  const diff = latestFinished(b).localeCompare(latestFinished(a));
  return diff !== 0
    ? diff
    : (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "");
};

// Plain recency order, newest first.
export const byRecent = (a: Tracking, b: Tracking): number =>
  (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "");

// Whether a log finishes its work: what the API recorded when the log was
// written (`finishes`, ADR 0030), so a show's season watched to its last
// episode finishes the season, not the show, and a finish stays one when a
// season gains an episode. The API sends it on every log since plan 010's
// TV-11 and TV-12, but a response the service worker cached before then
// (`public/service-worker.js`, shown offline) can lack it: such a log goes
// by the rule readers derived before, a whole read, watch or play, or an
// episode range that reaches the season's last episode (a season imported
// as one range, or watched an episode at a time). Never a drop.
export const finishesWork = (log: Log, work?: Work | null): boolean => {
  if (!isFinish(log)) return false;
  if (log.finishes != null) return log.finishes === "work";
  const end = log.segmentEnd ?? log.segmentStart;
  if (end == null) return true;
  const last = Number(work?.metadata?.episodeCount) || 0;
  return last > 0 && end >= last;
};

// How many times it's been finished: whole reads, watches or plays, and
// seasons watched to their last episode; not a drop.
export const timesFinished = (
  entry: Pick<Tracking, "logs" | "work">,
  work: Work | null | undefined = entry.work,
): number => entry.logs.filter((log) => finishesWork(log, work)).length;

const timesWord = (n: number): string =>
  n === 1 ? "once" : n === 2 ? "twice" : `${n} times`;

// The years it was finished in, oldest first, each once: two reads in one
// year are "Read twice · 2026", never "· 2026 · 2026".
export const finishYears = (work: Work, entry: Pick<Tracking, "logs">): string[] =>
  Array.from(
    new Set(
      entry.logs
        .filter((log) => finishesWork(log, work))
        .map((log) => localDay(log.finishedAt!).slice(0, 4)),
    ),
  ).sort();

// The furthest episode or chapter an entry's logs reach ("S2 E3", "ch. 12"),
// or null when none names one: where a share of its progress says it's up to.
const furthest = (logs: Log[], type: WorkType): string | null => {
  let best: Log | null = null;
  for (const log of logs) {
    if (log.segmentEnd == null) continue;
    if (
      !best ||
      (log.season ?? 0) > (best.season ?? 0) ||
      ((log.season ?? 0) === (best.season ?? 0) &&
        log.segmentEnd > best.segmentEnd!)
    )
      best = log;
  }
  return best
    ? segmentRange({ ...best, segmentStart: best.segmentEnd }, type)
    : null;
};

// What a work's knot says of an entry: how many times through, or where
// it stands (its tracking panel, a take and its share card).
export const standing = (
  work: Work,
  entry: Pick<Tracking, "status" | "logs">,
  times: number,
): string => {
  const type = work.workType;
  if (times > 0) return `${pastVerb(type)} ${timesWord(times)}`;
  if (entry.status === "in_progress") {
    const at = furthest(entry.logs, type);
    if (at) return `${progressVerb(type)}, up to ${at}`;
    const since = entry.logs.find((log) => !log.finishedAt)?.startedAt;
    return since
      ? `${progressVerb(type)} since ${formatTime(since)}`
      : progressVerb(type);
  }
  if (entry.status === "dropped") return "Dropped";
  return `Not ${pastVerb(type).toLowerCase()} yet`;
};

// A compact log summary: a count when read/watched/played through more than
// once (whole finishes, never the episodes or chapters on the way: plan
// 010's TV4), otherwise the latest engagement's date.
export const dateLabel = (entry: Tracking): string | null => {
  const type = entry.work?.workType;
  if (!type || entry.logs.length === 0) return null;

  const times = timesFinished(entry);
  if (times > 1) return `${pastVerb(type)} ${times}×`;

  const log = entry.logs[entry.logs.length - 1];
  if (log.finishedAt && log.dropped)
    return `Dropped ${formatTime(log.finishedAt)}`;
  if (log.finishedAt) return `${pastVerb(type)} ${formatTime(log.finishedAt)}`;
  if (log.startedAt)
    return `${progressVerb(type)} since ${formatTime(log.startedAt)}`;
  return null;
};

// How the library splits into shelves. "In progress" and the "up next" want
// list are manually orderable; the finished/abandoned shelves sort by date.
export type LibrarySection = {
  status: Status;
  title: string;
  sortable: boolean;
  sort: (a: Tracking, b: Tracking) => number;
};

export const LIBRARY_SECTIONS: LibrarySection[] = [
  {
    status: "in_progress",
    title: "In progress",
    sortable: true,
    sort: byPriority,
  },
  { status: "want", title: "Up next", sortable: true, sort: byPriority },
  {
    status: "completed",
    title: "Completed",
    sortable: false,
    sort: byCompletion,
  },
  { status: "dropped", title: "Dropped", sortable: false, sort: byRecent },
];

export const sectionFor = (status?: string): LibrarySection | undefined =>
  LIBRARY_SECTIONS.find((s) => s.status === status);

// What a "move to" action calls a status: its shelf name ("Up next"), except
// that finishing uses the media verb ("Read", "Watched") when every entry being
// moved is the same type.
export const moveLabel = (status: Status, entries: Tracking[]): string => {
  const types = new Set(entries.map((e) => e.work?.workType));
  const [type] = Array.from(types);
  if (status === "completed" && types.size === 1 && type) return pastVerb(type);
  return sectionFor(status)!.title;
};

// How many rows each shelf shows on the library overview before "Show more".
export const OVERVIEW_LIMIT = 6;

// How many more rows a full shelf reveals each time its bottom scrolls into view.
export const INFINITE_PAGE = 20;

// Fold case and accents so "pokemon" finds "Pokémon".
const fold = (text: string): string =>
  text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Whether an entry matches a library search: every word of `query` must
// appear in the work's title, subtitle, creators or series title.
export const matchesQuery = (entry: Tracking, query: string): boolean => {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const work = entry.work;
  if (!work) return false;
  const haystack = fold(
    [work.title, work.subtitle, creatorNames(work), groupingOf(work)?.title]
      .filter(Boolean)
      .join(" "),
  );
  return words.every((word) => haystack.includes(word));
};
